/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file referralSlice.js
 * @description Redux slice for managing referral related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the referral slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (referrals = null) => ({
  error: null,
  loading: false,
  referrals,
});

/**
 * @function getReferrals
 * @description Async thunk for get referrals operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getReferrals = createAsyncThunk("referral/loadReferral", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/referralmethods?originId=${originId}&lineId=${lineId}`);

    return {
      referrals: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain referral details", // serializable (err.response.data)
      name: "Referral List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant referralSlice
 * @description Redux slice for referral management
 */
const referralSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getReferrals.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.referrals = null;
      })
      .addCase(getReferrals.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getReferrals.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.referrals = action.payload.referrals;
      });
  },
  initialState: getInitialState(),
  name: "referral",
  reducers: {},
});
const { reducer } = referralSlice;
export default reducer;
